import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  Building2,
  FileText,
  Flag,
  FlaskConical,
  Heart,
  Home,
  LayoutDashboard,
  MessageSquare,
  Scale,
  Settings,
  Stethoscope,
  User,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';

import { createPageUrl } from '@/utils';

type TranslationFunction = (
  namespace: string,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => string;

type UserPermissions = {
  isAdmin: boolean;
  isMarketer: boolean;
};

export const getPrimaryLinks = (t: TranslationFunction) => [
  {
    key: 'home',
    to: createPageUrl('Home'),
    icon: Home,
    label: t('navigation', 'home', 'Home'),
  },
  {
    key: 'providers',
    to: createPageUrl('Providers'),
    icon: Building2,
    label: t('navigation', 'providers', 'Providers'),
    title: 'Browse medical providers and clinics in South Korea',
  },
  {
    key: 'doctors',
    to: createPageUrl('Doctors'),
    icon: Stethoscope,
    label: t('navigation', 'doctors', 'Doctors'),
  },
  {
    key: 'treatments',
    to: createPageUrl('Treatments'),
    icon: Wrench,
    label: t('navigation', 'treatments', 'Treatments'),
  },
];

export const getResourceLinks = (t: TranslationFunction) => [
  {
    key: 'about',
    to: createPageUrl('AboutUs'),
    icon: BookOpen,
    label: t('navigation', 'about_us', 'About Us'),
  },
  {
    key: 'blog',
    to: createPageUrl('Blog'),
    icon: FileText,
    label: t('navigation', 'blog', 'Blog'),
  },
  {
    key: 'changelog',
    to: createPageUrl('Changelog'),
    icon: FileText,
    label: t('navigation', 'changelog', 'Changelog'),
    title: 'View the latest updates and improvements to Medipaths',
  },
  {
    key: 'how_it_works',
    to: createPageUrl('HowItWorks'),
    icon: MessageSquare,
    label: t('navigation', 'how_it_works', 'How It Works'),
  },
];

export const getLegalLinks = (t: TranslationFunction) => [
  {
    key: 'terms',
    to: createPageUrl('TermsConditions'),
    icon: Scale,
    label: t('navigation', 'terms_conditions', 'Terms & Conditions'),
  },
  {
    key: 'privacy',
    to: createPageUrl('PrivacyPolicy'),
    icon: Scale,
    label: t('navigation', 'privacy_policy', 'Privacy Policy'),
  },
  {
    key: 'cookies',
    to: createPageUrl('CookiePolicy'),
    icon: Scale,
    label: t('navigation', 'cookie_policy', 'Cookie Policy'),
  },
  {
    key: 'medical',
    to: createPageUrl('MedicalDisclaimer'),
    icon: Scale,
    label: t('navigation', 'medical_disclaimer', 'Medical Disclaimer'),
  },
  {
    key: 'anti-discrimination',
    to: createPageUrl('AntiDiscrimination'),
    icon: Scale,
    label: t('navigation', 'anti_discrimination', 'Anti-Discrimination'),
  },
  {
    key: 'complaint-resolution',
    to: createPageUrl('ComplaintResolution'),
    icon: Scale,
    label: t('navigation', 'complaint_resolution', 'Complaint Resolution'),
  },
];

export const getToolsSettingsLinks = (t: TranslationFunction) => [
  {
    key: 'medipaths_lab',
    to: createPageUrl('MedipathsLab'),
    icon: FlaskConical,
    label: t('medipaths_lab', 'page_title', 'Medipaths Lab'),
  },
  {
    key: 'feature_requests',
    to: 'https://medipaths.canny.io/feature-requests',
    icon: MessageSquare,
    label: t('navigation', 'feature_requests', 'Feature Requests'),
    external: true,
    title: 'Submit and view feature requests for Medipaths',
  },
  {
    key: 'subprocessors',
    to: createPageUrl('Subprocessors'),
    icon: Settings,
    label: t('subprocessors', 'title', 'Subprocessors'),
  },
];

export const getPartnerLinks = (t: TranslationFunction) => {
  const baseUrl = createPageUrl('PartnerDashboard');
  return [
    {
      key: 'providers',
      to: `${baseUrl}/providers`,
      icon: Building2,
      label: t('partnerdashboard', 'providers_tab', 'Providers'),
    },
    {
      key: 'treatments',
      to: `${baseUrl}/treatments`,
      icon: Heart,
      label: t('partnerdashboard', 'treatments_tab', 'Treatments'),
    },
    {
      key: 'doctors',
      to: `${baseUrl}/doctors`,
      icon: User,
      label: t('partnerdashboard', 'doctors_tab', 'Doctors'),
    },
    {
      key: 'inquiries',
      to: `${baseUrl}/inquiries`,
      icon: MessageSquare,
      label: t('partnerdashboard', 'inquiries_tab', 'Inquiries'),
    },
    {
      key: 'analytics',
      to: `${baseUrl}/analytics`,
      icon: BarChart3,
      label: t('partnerdashboard', 'analytics_tab', 'Analytics'),
    },
  ];
};

export const getAdminLinks = (t: TranslationFunction, { isAdmin, isMarketer }: UserPermissions) => {
  const baseUrl = createPageUrl('AdminDashboard');

  if (!isAdmin && isMarketer) {
    return [
      {
        key: 'blog',
        to: `${baseUrl}#/blog`,
        icon: FileText,
        label: t('admin', 'blog', 'Blog'),
      },
    ];
  }

  if (!isAdmin) {
    return [];
  }

  return [
    {
      key: 'overview',
      to: `${baseUrl}#/overview`,
      icon: LayoutDashboard,
      label: t('admin', 'overview', 'Overview'),
    },
    {
      key: 'clinical',
      icon: Activity,
      to: `${baseUrl}#/clinical/providers`,
      label: t('admin', 'clinical', 'Clinical'),
      children: [
        {
          key: 'providers',
          to: `${baseUrl}#/clinical/providers`,
          icon: Building2,
          label: t('admin', 'providers', 'Providers'),
        },
        {
          key: 'doctors',
          to: `${baseUrl}#/clinical/doctors`,
          icon: Stethoscope,
          label: t('admin', 'doctors', 'Doctors'),
        },
        {
          key: 'procedures',
          to: `${baseUrl}#/clinical/procedures`,
          icon: Wrench,
          label: t('admin', 'procedures', 'Procedures'),
        },
        {
          key: 'treatments',
          to: `${baseUrl}#/clinical/treatments`,
          icon: Heart,
          label: t('admin', 'treatments', 'Treatments'),
        },
        {
          key: 'equipment',
          to: `${baseUrl}#/clinical/equipment`,
          icon: Activity,
          label: t('admin', 'equipment', 'Equipment'),
        },
      ],
    },
    {
      key: 'inquiries',
      to: `${baseUrl}#/inquiries`,
      icon: MessageSquare,
      label: t('admin', 'inquiries', 'Inquiries'),
    },
    {
      key: 'reports',
      to: `${baseUrl}#/reports`,
      icon: Flag,
      label: t('admin_reports', 'title', 'Reports'),
    },
    {
      key: 'chatbot',
      to: `${baseUrl}#/chatbot`,
      icon: Bot,
      label: t('admin', 'chatbot_title', 'Chatbot Analytics'),
    },
    {
      key: 'blog',
      to: `${baseUrl}#/blog`,
      icon: FileText,
      label: t('admin', 'blog', 'Blog'),
    },
    {
      key: 'usermanagement',
      icon: Users,
      to: `${baseUrl}#/usermanagement/partners`,
      label: t('admin', 'user_management', 'User Management'),
      children: [
        {
          key: 'partners',
          to: `${baseUrl}#/usermanagement/partners`,
          icon: Users,
          label: t('admin', 'partners', 'Partners'),
        },
        {
          key: 'userroles',
          to: `${baseUrl}#/usermanagement/userroles`,
          icon: UserCog,
          label: t('admin', 'user_roles', 'User Roles'),
        },
      ],
    },
    {
      key: 'webops',
      icon: Wrench,
      to: `${baseUrl}#/webops/pageseo`,
      label: t('admin', 'webops', 'Webops'),
      children: [
        {
          key: 'pageseo',
          to: `${baseUrl}#/webops/pageseo`,
          icon: FileText,
          label: t('admin', 'page_seo', 'Page SEO'),
        },
        {
          key: 'glossary',
          to: `${baseUrl}#/webops/glossary`,
          icon: BookOpen,
          label: t('admin', 'glossary', 'Glossary'),
        },
        {
          key: 'changelog',
          to: `${baseUrl}#/webops/changelog`,
          icon: FileText,
          label: t('admin', 'changelog', 'Changelog'),
        },
      ],
    },
  ];
};
